import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../useAuth";
import { canAccessAdminModules } from "../../accessControl";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import RichTextEditor from "../../ui/RichTextEditor";
import Select from "../../ui/Select";
import { spacing } from "../../ui/tokens";
import { formatApiError } from "../../api/apiClient";
import { getRiskMatrixConfig, updateRiskMatrixConfig } from "../../api/riskMatrixConfig.api";
import { getRiskRatingDefinitions, updateRiskRatingDefinitions } from "../../api/riskRatingDefinitions.api";
import {
  createImpactTheme,
  deleteImpactTheme,
  getImpactThemeLevels,
  listImpactThemes,
  updateImpactTheme,
  updateImpactThemeLevels,
} from "../../api/impactThemes.api";
import {
  DEFAULT_RISK_REVIEW_TEMPLATE,
  getRiskReviewTemplate,
  updateRiskReviewTemplate,
} from "../../api/riskReviewTemplate.api";
import {
  REVIEW_TEMPLATE_PLACEHOLDERS,
  extractTemplateTokenNames,
  ensureRichTextHtml,
  isRichTextEmpty,
  sanitizeRichTextHtml,
} from "../../utils/riskReviewTemplate";
import {
  DEFAULT_BANDS,
  DEFAULT_BAND_COLORS,
  bandToColor,
  getCellScore,
  getReadableTextColor,
  normalizeMatrixConfig,
  scoreToBand,
} from "../../utils/riskMatrixLogic";

const LEVELS = [1, 2, 3, 4, 5];
const DEFAULT_LIKELIHOOD = [
  { level: 1, label: "Rare", definition: "Unlikely to occur." },
  { level: 2, label: "Unlikely", definition: "Could occur but not expected." },
  { level: 3, label: "Possible", definition: "Might occur at some point." },
  { level: 4, label: "Likely", definition: "Expected in most circumstances." },
  { level: 5, label: "Almost Certain", definition: "Expected to occur frequently." },
];
const DEFAULT_IMPACT = [
  { level: 1, label: "Insignificant", definition: "Minimal impact." },
  { level: 2, label: "Minor", definition: "Limited disruption." },
  { level: 3, label: "Moderate", definition: "Noticeable disruption." },
  { level: 4, label: "Major", definition: "Significant disruption." },
  { level: 5, label: "Critical", definition: "Business-threatening impact." },
];

const sort5 = (entries, fallback) => {
  const byLevel = new Map((entries || []).map((entry) => [Number(entry.level), entry]));
  return LEVELS.map((level) => {
    const raw = byLevel.get(level) || fallback[level - 1];
    return {
      level,
      label: String(raw?.label || ""),
      definition: String(raw?.definition || ""),
    };
  });
};

const updateLevelField = (entries, level, field, value) =>
  entries.map((entry) => (Number(entry.level) === Number(level) ? { ...entry, [field]: value } : entry));

const autoGrow = (event) => {
  const el = event?.currentTarget;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
};

const buildCloneThemeName = (name, themes) => {
  const base = `${String(name || "Theme").trim()} Copy`;
  const existing = new Set((themes || []).map((theme) => String(theme?.name || "").toLowerCase()));
  let candidate = base;
  let counter = 2;
  while (existing.has(candidate.toLowerCase())) {
    candidate = `${base} ${counter}`;
    counter += 1;
  }
  return candidate;
};

const parseCellKey = (key) => {
  if (!key) return null;
  const [likelihood, impact] = String(key).split("-").map((part) => Number(part));
  if (!Number.isInteger(likelihood) || !Number.isInteger(impact)) return null;
  return { likelihood, impact };
};

const defaultBandColorAt = (index) =>
  DEFAULT_BANDS[Math.min(Math.max(index, 0), DEFAULT_BANDS.length - 1)]?.color || DEFAULT_BAND_COLORS.critical;

const PLACEHOLDER_OPTIONS = REVIEW_TEMPLATE_PLACEHOLDERS.map((placeholder) => ({
  label: placeholder.label,
  value: placeholder.name,
}));

export default function RiskMatrixAdmin() {
  const { user } = useAuth();
  const canManageAdminModules = canAccessAdminModules(user);
  const isAdmin = canManageAdminModules;

  const [matrixConfig, setMatrixConfig] = useState(() => normalizeMatrixConfig(null));
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixLoadError, setMatrixLoadError] = useState(null);
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [matrixMessage, setMatrixMessage] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingCellValue, setEditingCellValue] = useState("");

  const [ratings, setRatings] = useState(() => ({
    likelihood: sort5([], DEFAULT_LIKELIHOOD),
    impact: sort5([], DEFAULT_IMPACT),
    version: null,
  }));
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsLoadError, setRatingsLoadError] = useState(null);
  const [activeLikelihoodLevel, setActiveLikelihoodLevel] = useState(1);
  const [ratingSaving, setRatingSaving] = useState(false);
  const [ratingMessage, setRatingMessage] = useState(null);

  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [themesLoadError, setThemesLoadError] = useState(null);
  const [activeThemeId, setActiveThemeId] = useState(null);
  const [themeName, setThemeName] = useState("");
  const [themeLevels, setThemeLevels] = useState(() => sort5([], DEFAULT_IMPACT));
  const [themeLevelsLoading, setThemeLevelsLoading] = useState(false);
  const [themeLevelsError, setThemeLevelsError] = useState(null);
  const [activeImpactLevel, setActiveImpactLevel] = useState(1);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMessage, setThemeMessage] = useState(null);
  const [addingTheme, setAddingTheme] = useState(false);
  const [newTheme, setNewTheme] = useState("");
  const [themeAction, setThemeAction] = useState("");
  const [showRenameTheme, setShowRenameTheme] = useState(false);
  const [reviewTemplate, setReviewTemplate] = useState(() => ({
    template_body: ensureRichTextHtml(DEFAULT_RISK_REVIEW_TEMPLATE.template_body),
    impact_section_default: ensureRichTextHtml(DEFAULT_RISK_REVIEW_TEMPLATE.impact_section_default),
    mitigation_section_default: ensureRichTextHtml(DEFAULT_RISK_REVIEW_TEMPLATE.mitigation_section_default),
  }));
  const [reviewTemplateLoading, setReviewTemplateLoading] = useState(false);
  const [reviewTemplateMessage, setReviewTemplateMessage] = useState(null);
  const [reviewTemplateSaving, setReviewTemplateSaving] = useState(false);

  const likelihood = useMemo(() => sort5(ratings?.likelihood, DEFAULT_LIKELIHOOD), [ratings]);
  const impactLevels = useMemo(() => sort5(themeLevels, DEFAULT_IMPACT), [themeLevels]);
  const selectedLikelihoodLevel = useMemo(
    () => likelihood.find((entry) => Number(entry.level) === Number(activeLikelihoodLevel)) || likelihood[0] || null,
    [likelihood, activeLikelihoodLevel]
  );
  const selectedImpactLevel = useMemo(
    () => impactLevels.find((entry) => Number(entry.level) === Number(activeImpactLevel)) || impactLevels[0] || null,
    [impactLevels, activeImpactLevel]
  );
  const activeTheme = useMemo(
    () => themes.find((theme) => Number(theme.id) === Number(activeThemeId)) || null,
    [themes, activeThemeId]
  );
  const createDisabled = themeSaving || !String(newTheme || "").trim();
  const usedReviewTemplateTokens = useMemo(
    () =>
      extractTemplateTokenNames(
        reviewTemplate?.template_body || "",
        REVIEW_TEMPLATE_PLACEHOLDERS.map((token) => token.name)
      ),
    [reviewTemplate?.template_body]
  );

  const themeErrorText = (error, action) =>
    formatApiError(error, {
      action,
      fallbackDetail: "Please try again.",
    });

  useEffect(() => {
    if (!canManageAdminModules) return;
    let cancelled = false;

    const loadMatrixConfigData = async () => {
      setMatrixLoading(true);
      setMatrixLoadError(null);
      try {
        const res = await getRiskMatrixConfig();
        if (cancelled) return;
        const payload = res?.data || res;
        setMatrixConfig(normalizeMatrixConfig(payload));
      } catch (err) {
        if (cancelled) return;
        setMatrixConfig(normalizeMatrixConfig(null));
        setMatrixLoadError(err?.response?.data?.detail || err?.message || "Unable to load risk matrix configuration.");
      } finally {
        if (!cancelled) setMatrixLoading(false);
      }
    };

    const loadRatingDefinitions = async () => {
      setRatingsLoading(true);
      setRatingsLoadError(null);
      try {
        const res = await getRiskRatingDefinitions();
        if (cancelled) return;
        const payload = res?.data || res;
        setRatings({
          likelihood: sort5(payload?.likelihood, DEFAULT_LIKELIHOOD),
          impact: sort5(payload?.impact, DEFAULT_IMPACT),
          version: payload?.version ?? null,
        });
      } catch (err) {
        if (cancelled) return;
        setRatingsLoadError(err?.response?.data?.detail || err?.message || "Unable to load likelihood definitions.");
      } finally {
        if (!cancelled) setRatingsLoading(false);
      }
    };

    const loadThemes = async () => {
      setThemesLoading(true);
      setThemesLoadError(null);
      try {
        const res = await listImpactThemes();
        if (cancelled) return;
        const payload = res?.data || res || [];
        const list = Array.isArray(payload) ? payload : [];
        setThemes(list);
        setActiveThemeId((prev) => {
          const keep = list.find((theme) => Number(theme.id) === Number(prev));
          if (keep) return keep.id;
          const fallback = list.find((theme) => theme.is_default) || list[0] || null;
          return fallback?.id ?? null;
        });
      } catch (err) {
        if (cancelled) return;
        setThemes([]);
        setThemesLoadError(themeErrorText(err, "Unable to load impact themes"));
      } finally {
        if (!cancelled) setThemesLoading(false);
      }
    };

    const loadReviewTemplate = async () => {
      setReviewTemplateLoading(true);
      setReviewTemplateMessage(null);
      try {
        const res = await getRiskReviewTemplate({ force: true });
        if (cancelled) return;
        const payload = res?.data || {};
        setReviewTemplate({
          template_body: ensureRichTextHtml(payload?.template_body || DEFAULT_RISK_REVIEW_TEMPLATE.template_body),
          impact_section_default: ensureRichTextHtml(
            payload?.impact_section_default || DEFAULT_RISK_REVIEW_TEMPLATE.impact_section_default
          ),
          mitigation_section_default: ensureRichTextHtml(
            payload?.mitigation_section_default || DEFAULT_RISK_REVIEW_TEMPLATE.mitigation_section_default
          ),
        });
      } catch (err) {
        if (cancelled) return;
        setReviewTemplate({
          template_body: ensureRichTextHtml(DEFAULT_RISK_REVIEW_TEMPLATE.template_body),
          impact_section_default: ensureRichTextHtml(DEFAULT_RISK_REVIEW_TEMPLATE.impact_section_default),
          mitigation_section_default: ensureRichTextHtml(DEFAULT_RISK_REVIEW_TEMPLATE.mitigation_section_default),
        });
        setReviewTemplateMessage({
          tone: "error",
          text: err?.response?.data?.detail || err?.message || "Unable to load assessment review template.",
        });
      } finally {
        if (!cancelled) setReviewTemplateLoading(false);
      }
    };

    loadMatrixConfigData();
    loadRatingDefinitions();
    loadThemes();
    loadReviewTemplate();

    return () => {
      cancelled = true;
    };
  }, [canManageAdminModules]);

  useEffect(() => {
    if (!isAdmin || !activeThemeId) return;
    let cancelled = false;

    const loadThemeLevels = async () => {
      setThemeLevelsLoading(true);
      setThemeLevelsError(null);
      try {
        const res = await getImpactThemeLevels(activeThemeId);
        if (cancelled) return;
        const payload = res?.data || res;
        setThemeName(payload?.theme?.name || "");
        setThemeLevels(sort5(payload?.levels, DEFAULT_IMPACT));
        setShowRenameTheme(false);
      } catch (err) {
        if (cancelled) return;
        setThemeLevels(sort5([], DEFAULT_IMPACT));
        setThemeLevelsError(themeErrorText(err, "Unable to load theme levels"));
      } finally {
        if (!cancelled) setThemeLevelsLoading(false);
      }
    };

    loadThemeLevels();

    return () => {
      cancelled = true;
    };
  }, [activeThemeId, isAdmin]);

  const refreshThemes = async (preferredThemeId = null) => {
    const res = await listImpactThemes();
    const payload = res?.data || res || [];
    const list = Array.isArray(payload) ? payload : [];
    setThemes(list);
    const next =
      list.find((theme) => Number(theme.id) === Number(preferredThemeId)) ||
      list.find((theme) => theme.is_default) ||
      list[0] ||
      null;
    setActiveThemeId(next?.id ?? null);
  };

  const saveLikelihood = async () => {
    setRatingSaving(true);
    setRatingMessage(null);
    try {
      const payload = {
        likelihood: likelihood.map((entry) => ({
          level: entry.level,
          label: String(entry.label || "").trim(),
          definition: String(entry.definition || "").trim(),
        })),
        impact: sort5(ratings?.impact, DEFAULT_IMPACT),
      };
      const res = await updateRiskRatingDefinitions(payload);
      const data = res?.data || res;
      setRatings({
        likelihood: sort5(data?.likelihood || payload.likelihood, DEFAULT_LIKELIHOOD),
        impact: sort5(data?.impact || payload.impact, DEFAULT_IMPACT),
        version: data?.version ?? ratings?.version,
      });
      setRatingMessage({ tone: "success", text: "Likelihood definitions saved." });
      window.dispatchEvent(new CustomEvent("riskRatingDefinitionsChanged"));
    } catch (err) {
      setRatingMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Failed to save likelihood definitions.",
      });
    } finally {
      setRatingSaving(false);
    }
  };

  const saveThemeLevels = async () => {
    if (!activeThemeId) return;
    setThemeSaving(true);
    setThemeMessage(null);
    try {
      await updateImpactThemeLevels(activeThemeId, {
        levels: impactLevels.map((entry) => ({
          level: Number(entry.level),
          label: String(entry.label || "").trim(),
          definition: String(entry.definition || "").trim(),
        })),
      });
      setThemeMessage({ tone: "success", text: "Impact theme definitions saved." });
      window.dispatchEvent(new CustomEvent("impactThemesChanged"));
    } catch (err) {
      setThemeMessage({
        tone: "error",
        text: themeErrorText(err, "Failed to save impact theme definitions"),
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const renameTheme = async () => {
    if (!activeThemeId) return;
    setThemeSaving(true);
    setThemeMessage(null);
    try {
      await updateImpactTheme(activeThemeId, { name: String(themeName || "").trim() });
      await refreshThemes(activeThemeId);
      setShowRenameTheme(false);
      setThemeMessage({ tone: "success", text: "Theme renamed." });
      window.dispatchEvent(new CustomEvent("impactThemesChanged"));
    } catch (err) {
      setThemeMessage({
        tone: "error",
        text: themeErrorText(err, "Failed to rename theme"),
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const cloneTheme = async () => {
    if (!activeThemeId || !activeTheme) return;
    setThemeSaving(true);
    setThemeMessage(null);
    try {
      const name = buildCloneThemeName(activeTheme.name, themes);
      const createRes = await createImpactTheme({ name });
      const created = createRes?.data || createRes;
      if (created?.id) {
        await updateImpactThemeLevels(created.id, {
          levels: impactLevels.map((entry) => ({
            level: Number(entry.level),
            label: String(entry.label || "").trim(),
            definition: String(entry.definition || "").trim(),
          })),
        });
      }
      await refreshThemes(created?.id);
      setThemeMessage({ tone: "success", text: "Theme cloned." });
      window.dispatchEvent(new CustomEvent("impactThemesChanged"));
    } catch (err) {
      setThemeMessage({
        tone: "error",
        text: themeErrorText(err, "Failed to clone theme"),
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const addTheme = async () => {
    const name = String(newTheme || "").trim();
    if (!name) return;
    setThemeSaving(true);
    setThemeMessage(null);
    try {
      const res = await createImpactTheme({ name });
      const data = res?.data || res;
      await refreshThemes(data?.id);
      setAddingTheme(false);
      setNewTheme("");
      setThemeMessage({ tone: "success", text: "Impact theme created." });
      window.dispatchEvent(new CustomEvent("impactThemesChanged"));
    } catch (err) {
      setThemeMessage({
        tone: "error",
        text: themeErrorText(err, "Theme creation failed"),
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const removeTheme = async () => {
    if (!activeThemeId) return;
    if (Number(activeTheme?.risk_count || 0) > 0) {
      setThemeMessage({ tone: "error", text: "Cannot delete theme while it is in use by risks." });
      return;
    }
    setThemeSaving(true);
    setThemeMessage(null);
    try {
      await deleteImpactTheme(activeThemeId);
      await refreshThemes();
      setThemeMessage({ tone: "success", text: "Theme deleted." });
      window.dispatchEvent(new CustomEvent("impactThemesChanged"));
    } catch (err) {
      setThemeMessage({
        tone: "error",
        text: themeErrorText(err, "Failed to delete theme"),
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const onThemeActionChange = async (nextAction) => {
    setThemeAction(nextAction);
    if (nextAction === "rename") {
      setShowRenameTheme(true);
      setThemeAction("");
      return;
    }
    if (nextAction === "clone") {
      setThemeAction("");
      await cloneTheme();
      return;
    }
    if (nextAction === "delete") {
      setThemeAction("");
      await removeTheme();
      return;
    }
    setThemeAction("");
  };

  const saveMatrix = async () => {
    setMatrixSaving(true);
    setMatrixMessage(null);
    try {
      const currentBands = Array.isArray(matrixConfig?.band_thresholds) && matrixConfig.band_thresholds.length
        ? matrixConfig.band_thresholds
        : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
      let previousMax = 0;
      const normalizedBands = currentBands.map((band, index) => {
        const rawName = String(band?.name || "").trim();
        const name = rawName || `Band ${index + 1}`;
        if (index === currentBands.length - 1) {
          return {
            name,
            max_score: null,
          };
        }
        const parsed = Number(band?.max_score);
        const fallback = previousMax + 5;
        const max = Number.isFinite(parsed) && parsed > previousMax ? parsed : fallback;
        previousMax = max;
        return {
          name,
          max_score: max,
        };
      });
      const normalizedColors = normalizedBands.reduce((acc, band, index) => {
        acc[band.name] = matrixConfig?.band_colors?.[band.name] || defaultBandColorAt(index);
        return acc;
      }, {});

      const payload = {
        matrix_size: Number(matrixConfig?.matrix_size) || 5,
        scoring_mode: matrixConfig?.scoring_mode === "cell_map" ? "cell_map" : "multiplicative",
        cell_scores: matrixConfig?.cell_scores,
        band_thresholds: normalizedBands,
        band_colors: normalizedColors,
      };
      const res = await updateRiskMatrixConfig(payload);
      const data = res?.data || res;
      setMatrixConfig(normalizeMatrixConfig(data));
      setMatrixMessage({ tone: "success", text: "Risk matrix configuration saved." });
      window.dispatchEvent(new CustomEvent("riskMatrixConfigChanged"));
    } catch (err) {
      setMatrixMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Failed to save risk matrix configuration.",
      });
    } finally {
      setMatrixSaving(false);
    }
  };

  const saveReviewTemplate = async (nextTemplate) => {
    if (reviewTemplateSaving) return;
    const payload = {
      template_body: sanitizeRichTextHtml(nextTemplate?.template_body || ""),
      impact_section_default: sanitizeRichTextHtml(nextTemplate?.impact_section_default || ""),
      mitigation_section_default: sanitizeRichTextHtml(nextTemplate?.mitigation_section_default || ""),
    };
    if (isRichTextEmpty(payload.template_body)) {
      setReviewTemplateMessage({
        tone: "error",
        text: "Review summary template is required.",
      });
      return;
    }

    setReviewTemplateSaving(true);
    setReviewTemplateMessage(null);
    try {
      const res = await updateRiskReviewTemplate(payload);
      const data = res?.data || payload;
      const normalized = {
        template_body: ensureRichTextHtml(data?.template_body || DEFAULT_RISK_REVIEW_TEMPLATE.template_body),
        impact_section_default: ensureRichTextHtml(
          data?.impact_section_default || DEFAULT_RISK_REVIEW_TEMPLATE.impact_section_default
        ),
        mitigation_section_default: ensureRichTextHtml(
          data?.mitigation_section_default || DEFAULT_RISK_REVIEW_TEMPLATE.mitigation_section_default
        ),
      };
      setReviewTemplate(normalized);
      setReviewTemplateMessage({ tone: "success", text: "Assessment review template saved." });
      window.dispatchEvent(new CustomEvent("riskReviewTemplateChanged"));
    } catch (err) {
      setReviewTemplateMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Failed to save assessment review template.",
      });
    } finally {
      setReviewTemplateSaving(false);
    }
  };

  const resetReviewTemplate = async () => {
    await saveReviewTemplate({ ...DEFAULT_RISK_REVIEW_TEMPLATE });
  };

  const setBandName = (index, value) => {
    setMatrixConfig((prev) => {
      const bands = Array.isArray(prev?.band_thresholds) && prev.band_thresholds.length
        ? [...prev.band_thresholds]
        : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
      const existing = bands[index];
      if (!existing) return prev;
      const nextName = String(value || "");
      bands[index] = { ...existing, name: nextName };
      const colors = { ...(prev?.band_colors || {}) };
      const oldName = String(existing?.name || "");
      const currentColor =
        colors[oldName] ||
        colors[nextName] ||
        defaultBandColorAt(index);
      if (oldName && oldName !== nextName) {
        delete colors[oldName];
      }
      colors[nextName] = currentColor;
      return {
        ...prev,
        band_thresholds: bands,
        band_colors: colors,
      };
    });
  };

  const setBandMax = (index, value) => {
    setMatrixConfig((prev) => {
      const bands = Array.isArray(prev?.band_thresholds) && prev.band_thresholds.length
        ? [...prev.band_thresholds]
        : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
      const existing = bands[index];
      if (!existing) return prev;
      const parsed = Number(value);
      bands[index] = {
        ...existing,
        max_score: Number.isFinite(parsed) ? parsed : existing?.max_score,
      };
      return {
        ...prev,
        band_thresholds: bands,
      };
    });
  };

  const setBandColor = (index, value) => {
    setMatrixConfig((prev) => {
      const bands = Array.isArray(prev?.band_thresholds) && prev.band_thresholds.length
        ? prev.band_thresholds
        : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
      const bandName = String(bands[index]?.name || "").trim();
      if (!bandName) return prev;
      return {
        ...prev,
        band_colors: {
          ...(prev?.band_colors || {}),
          [bandName]: value,
        },
      };
    });
  };

  const addBand = () => {
    setMatrixConfig((prev) => {
      const bands = Array.isArray(prev?.band_thresholds) && prev.band_thresholds.length
        ? [...prev.band_thresholds]
        : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
      const colors = { ...(prev?.band_colors || {}) };
      const lastIdx = bands.length - 1;
      const prevMaxRaw = Number(bands[lastIdx - 1]?.max_score);
      const fallbackBase = Number.isFinite(prevMaxRaw) ? prevMaxRaw : 5 * Math.max(1, bands.length - 1);
      const currentLastRaw = bands[lastIdx]?.max_score;
      const currentLastMax = currentLastRaw == null ? null : Number(currentLastRaw);
      if (!Number.isFinite(currentLastMax)) {
        bands[lastIdx] = { ...bands[lastIdx], max_score: fallbackBase + 5 };
      }
      const name = `Band ${bands.length + 1}`;
      bands.push({ name, max_score: null });
      colors[name] = defaultBandColorAt(bands.length - 1);
      return {
        ...prev,
        band_thresholds: bands,
        band_colors: colors,
      };
    });
  };

  const removeBand = (index) => {
    setMatrixConfig((prev) => {
      const bands = Array.isArray(prev?.band_thresholds) && prev.band_thresholds.length
        ? [...prev.band_thresholds]
        : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
      if (bands.length <= 2) return prev;
      const [removed] = bands.splice(index, 1);
      const lastIdx = bands.length - 1;
      bands[lastIdx] = { ...bands[lastIdx], max_score: null };
      const colors = { ...(prev?.band_colors || {}) };
      if (removed?.name) {
        delete colors[removed.name];
      }
      return {
        ...prev,
        band_thresholds: bands,
        band_colors: colors,
      };
    });
  };

  const openCellEditor = (likelihoodLevel, impactLevel) => {
    if (matrixConfig?.scoring_mode !== "cell_map") return;
    const value = matrixConfig?.cell_scores?.[String(likelihoodLevel)]?.[String(impactLevel)] ?? "";
    setEditingCell(`${likelihoodLevel}-${impactLevel}`);
    setEditingCellValue(String(value));
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setEditingCellValue("");
  };

  const commitCellEdit = () => {
    const parsed = parseCellKey(editingCell);
    if (!parsed) {
      cancelCellEdit();
      return;
    }
    const value = Number(editingCellValue);
    if (Number.isFinite(value)) {
      setMatrixConfig((prev) => ({
        ...prev,
        cell_scores: {
          ...(prev?.cell_scores || {}),
          [String(parsed.likelihood)]: {
            ...((prev?.cell_scores || {})[String(parsed.likelihood)] || {}),
            [String(parsed.impact)]: value,
          },
        },
      }));
    }
    cancelCellEdit();
  };

  if (!canManageAdminModules) {
    return (
      <div style={{ padding: spacing.lg * 2 }}>
        <h2 style={{ marginTop: 0 }}>Risk Matrix</h2>
        <p style={{ color: "#6b7280" }}>Requires Manage access to Admin Module Portal.</p>
      </div>
    );
  }

  const bandRows = Array.isArray(matrixConfig?.band_thresholds) && matrixConfig.band_thresholds.length
    ? matrixConfig.band_thresholds
    : DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
  const deleteDisabled = Number(activeTheme?.risk_count || 0) > 0;

  return (
    <div style={pageWrap}>
      <h2 style={{ margin: 0 }}>Risk Matrix</h2>
      <p style={subtitle}>Manage how risks are scored and defined across your organisation</p>

      <section style={sectionCard}>
        <h3 style={sectionTitle}>Section 1 - Scoring & Bands</h3>

        <div style={scoringRow}>
          <div style={modeGroup} role="radiogroup" aria-label="Scoring mode">
            <label style={radioItem}>
              <input
                type="radio"
                name="scoring_mode"
                checked={matrixConfig?.scoring_mode !== "cell_map"}
                onChange={() => setMatrixConfig((prev) => ({ ...prev, scoring_mode: "multiplicative" }))}
              />
              <span>Multiplicative</span>
            </label>
            <label style={radioItem}>
              <input
                type="radio"
                name="scoring_mode"
                checked={matrixConfig?.scoring_mode === "cell_map"}
                onChange={() => setMatrixConfig((prev) => ({ ...prev, scoring_mode: "cell_map" }))}
              />
              <span>Custom Cell Map</span>
            </label>
          </div>
          <Button onClick={saveMatrix} variant="primary" disabled={matrixSaving || matrixLoading}>
            {matrixSaving ? "Saving..." : "Save Risk Matrix Configuration"}
          </Button>
        </div>

        {matrixMessage && <Status message={matrixMessage} />}
        {matrixLoadError && <InlineError text={matrixLoadError} />}

        <div style={bandsHeaderRow}>
          <div style={helperText}>Admins can rename bands and add more scoring bands when needed.</div>
          <Button type="button" variant="secondary" onClick={addBand} disabled={matrixSaving || matrixLoading}>
            + Add Band
          </Button>
        </div>

        <div style={bandsGrid}>
          {bandRows.map((band, index) => {
            const previous = index > 0 ? Number(bandRows[index - 1]?.max_score) : 0;
            const start = index === 0 ? 1 : (Number.isFinite(previous) ? previous + 1 : "?");
            const isLast = index === bandRows.length - 1;
            const max = Number(band?.max_score);
            const rangeLabel = isLast ? `${start}+` : `${start} -`;
            const colorValue =
              matrixConfig?.band_colors?.[band?.name] ||
              defaultBandColorAt(index);

            return (
              <React.Fragment key={`band-row-${index}`}>
                <Input
                  aria-label={`Band ${index + 1} name`}
                  value={band?.name || ""}
                  onChange={(e) => setBandName(index, e.target.value)}
                />
                <div style={rangeRow}>
                  <span>{rangeLabel}</span>
                  {!isLast && (
                    <Input
                      aria-label={`Band ${index + 1} max`}
                      type="number"
                      value={Number.isFinite(max) ? max : ""}
                      onChange={(e) => setBandMax(index, e.target.value)}
                    />
                  )}
                </div>
                <input
                  aria-label={`Band ${index + 1} color`}
                  type="color"
                  value={colorValue}
                  onChange={(e) => setBandColor(index, e.target.value)}
                  style={colorInput}
                />
                <div style={bandActionCell}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeBand(index)}
                    disabled={bandRows.length <= 2 || matrixSaving || matrixLoading}
                  >
                    Remove
                  </Button>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div style={matrixWrap}>
          <div style={matrixHeadRow}>
            <div style={matrixCorner}>L \ I</div>
            {LEVELS.map((impactLevel) => (
              <div key={`impact-${impactLevel}`} style={matrixAxis}>Impact {impactLevel}</div>
            ))}
          </div>

          {LEVELS.map((likelihoodLevel) => (
            <div key={`row-${likelihoodLevel}`} style={matrixRow}>
              <div style={matrixAxis}>Likelihood {likelihoodLevel}</div>
              {LEVELS.map((impactLevel) => {
                const cellKey = `${likelihoodLevel}-${impactLevel}`;
                const isEditing = editingCell === cellKey;
                const score = getCellScore({
                  scoringMode: matrixConfig?.scoring_mode,
                  cellScores: matrixConfig?.cell_scores,
                  lik: likelihoodLevel,
                  imp: impactLevel,
                });
                const band = scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds });
                const bg = bandToColor({ band, bandColors: matrixConfig?.band_colors });
                const textColor = getReadableTextColor(bg);
                const displayScore = Number.isFinite(score) ? String(score) : "-";

                return (
                  <div key={`cell-${cellKey}`} style={{ ...matrixCellShell, background: bg }}>
                    {!isEditing && (
                      <button
                        type="button"
                        aria-label={`Matrix cell L${likelihoodLevel} I${impactLevel}`}
                        onClick={() => openCellEditor(likelihoodLevel, impactLevel)}
                        disabled={matrixConfig?.scoring_mode !== "cell_map"}
                        style={{
                          ...matrixCellButton,
                          color: textColor,
                          cursor: matrixConfig?.scoring_mode === "cell_map" ? "pointer" : "default",
                        }}
                      >
                        {displayScore}
                      </button>
                    )}
                    {isEditing && (
                      <Input
                        aria-label={`Cell score L${likelihoodLevel} I${impactLevel}`}
                        type="number"
                        autoFocus
                        value={editingCellValue}
                        onChange={(event) => setEditingCellValue(event.target.value)}
                        onBlur={commitCellEdit}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") commitCellEdit();
                          if (event.key === "Escape") cancelCellEdit();
                        }}
                        style={matrixCellInput}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={templateSection}>
          <h4 style={panelTitle}>Assessment Review Template</h4>
          <p style={helperText}>
            Configure the pre-populated note shown when assessment scoring changes.
          </p>
          <div style={placeholderReference}>
            <div style={placeholderReferenceTitle}>Placeholder Reference</div>
            <div style={placeholderReferenceGrid}>
              {REVIEW_TEMPLATE_PLACEHOLDERS.map((token) => (
                <div
                  key={token.name}
                  style={{
                    ...placeholderCard,
                    ...(usedReviewTemplateTokens.has(token.name) ? placeholderCardActive : null),
                  }}
                >
                  <div style={placeholderCardHeader}>
                    <div style={placeholderCardLabel}>{token.label}</div>
                    {usedReviewTemplateTokens.has(token.name) && <span style={placeholderBadge}>In use</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={editorLabel}>
            <span style={editorLabelText}>Review Summary Template</span>
            <RichTextEditor
              value={reviewTemplate.template_body}
              onChange={(nextValue) =>
                setReviewTemplate((prev) => ({ ...prev, template_body: nextValue || "<p></p>" }))
              }
              disabled={reviewTemplateLoading || reviewTemplateSaving}
              placeholderTokens={PLACEHOLDER_OPTIONS}
              minHeight={160}
            />
          </div>
          <div style={editorLabel}>
            <span style={editorLabelText}>Impact to Business Default</span>
            <RichTextEditor
              value={reviewTemplate.impact_section_default}
              onChange={(nextValue) =>
                setReviewTemplate((prev) => ({ ...prev, impact_section_default: nextValue || "<p></p>" }))
              }
              disabled={reviewTemplateLoading || reviewTemplateSaving}
              minHeight={110}
            />
          </div>
          <div style={editorLabel}>
            <span style={editorLabelText}>Mitigation / Remediation Default</span>
            <RichTextEditor
              value={reviewTemplate.mitigation_section_default}
              onChange={(nextValue) =>
                setReviewTemplate((prev) => ({ ...prev, mitigation_section_default: nextValue || "<p></p>" }))
              }
              disabled={reviewTemplateLoading || reviewTemplateSaving}
              minHeight={110}
            />
          </div>
          <div style={templateButtons}>
            <Button
              type="button"
              variant="secondary"
              onClick={resetReviewTemplate}
              disabled={reviewTemplateSaving || reviewTemplateLoading}
            >
              Reset to Default
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => saveReviewTemplate(reviewTemplate)}
              disabled={reviewTemplateSaving || reviewTemplateLoading}
            >
              {reviewTemplateSaving ? "Saving..." : "Save Template"}
            </Button>
          </div>
          {reviewTemplateMessage && <Status message={reviewTemplateMessage} />}
        </div>
      </section>

      <section style={sectionCard}>
        <h3 style={sectionTitle}>Section 2 - Definitions</h3>
        <div style={definitionsGrid}>
          <div style={definitionPanel}>
            <h4 style={panelTitle}>Likelihood Ratings</h4>
            {ratingsLoadError && <InlineError text={ratingsLoadError} />}
            {ratingsLoading && <div style={helperText}>Loading likelihood definitions...</div>}
            <div style={panelEditorWrap}>
              <LevelSidebar
                title="Likelihood Levels"
                entries={likelihood}
                selectedLevel={activeLikelihoodLevel}
                onSelect={setActiveLikelihoodLevel}
                ariaPrefix="Select likelihood level"
              />
              <LevelEditor
                heading={`Level ${selectedLikelihoodLevel?.level || 1}`}
                labelValue={selectedLikelihoodLevel?.label || ""}
                definitionValue={selectedLikelihoodLevel?.definition || ""}
                labelInputLabel="Label"
                definitionLabel="Definition"
                onLabelChange={(value) =>
                  setRatings((prev) => ({
                    ...prev,
                    likelihood: updateLevelField(likelihood, selectedLikelihoodLevel?.level, "label", value),
                  }))
                }
                onDefinitionChange={(value) =>
                  setRatings((prev) => ({
                    ...prev,
                    likelihood: updateLevelField(likelihood, selectedLikelihoodLevel?.level, "definition", value),
                  }))
                }
                saveLabel={ratingSaving ? "Saving..." : "Save Likelihood Scale"}
                onSave={saveLikelihood}
                disabled={ratingSaving || ratingsLoading}
              />
            </div>
            {ratingMessage && <Status message={ratingMessage} />}
          </div>

          <div style={definitionPanel}>
            <div style={impactHeader}>
              <h4 style={panelTitle}>Impact Themes</h4>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setAddingTheme(true);
                  setThemeMessage(null);
                }}
                disabled={addingTheme}
              >
                + Add Theme
              </Button>
            </div>

            {themesLoadError && <InlineError text={themesLoadError} />}
            {themesLoading && <div style={helperText}>Loading impact themes...</div>}

            {addingTheme && (
              <div style={inlineStrip}>
                <Input label="New Theme Name" value={newTheme} onChange={(event) => setNewTheme(event.target.value)} />
                <div style={inlineButtons}>
                  <Button
                    type="button"
                    onClick={() => {
                      setAddingTheme(false);
                      setNewTheme("");
                    }}
                    variant="ghost"
                    disabled={themeSaving}
                  >
                    Cancel
                  </Button>
                  <Button type="button" onClick={addTheme} variant="primary" disabled={createDisabled}>
                    {themeSaving ? "Creating..." : "Create Theme"}
                  </Button>
                </div>
              </div>
            )}

            <div style={themeTabsWrap}>
              {themes.map((theme) => {
                const selected = Number(theme.id) === Number(activeThemeId);
                return (
                  <button
                    key={`theme-${theme.id}`}
                    type="button"
                    onClick={() => setActiveThemeId(theme.id)}
                    style={selected ? themeTabActive : themeTab}
                  >
                    {theme.name}
                  </button>
                );
              })}
            </div>

            {activeTheme && (
              <>
                <div style={themeControls}>
                  <div style={helperText}>Active Theme: {activeTheme.name}</div>
                  <div style={{ width: 170 }}>
                    <Select
                      aria-label="Theme Actions"
                      value={themeAction}
                      onChange={(event) => onThemeActionChange(event.target.value)}
                    >
                      <option value="">Theme actions</option>
                      <option value="rename">Rename</option>
                      <option value="clone">Clone</option>
                      <option value="delete" disabled={deleteDisabled}>Delete</option>
                    </Select>
                  </div>
                </div>

                {deleteDisabled && <div style={helperText}>Delete is disabled while this theme is used by risks.</div>}

                {showRenameTheme && (
                  <div style={inlineStrip}>
                    <Input label="Theme Name" value={themeName} onChange={(event) => setThemeName(event.target.value)} />
                    <div style={inlineButtons}>
                      <Button onClick={renameTheme} variant="secondary" disabled={themeSaving || themeLevelsLoading}>
                        Save Name
                      </Button>
                      <Button onClick={() => setShowRenameTheme(false)} variant="ghost" disabled={themeSaving}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                <div style={panelEditorWrap}>
                  <LevelSidebar
                    title="Impact Levels"
                    entries={impactLevels}
                    selectedLevel={activeImpactLevel}
                    onSelect={setActiveImpactLevel}
                    ariaPrefix="Select impact level"
                  />
                  <LevelEditor
                    heading={`Level ${selectedImpactLevel?.level || 1}`}
                    labelValue={selectedImpactLevel?.label || ""}
                    definitionValue={selectedImpactLevel?.definition || ""}
                    labelInputLabel="Label"
                    definitionLabel="Definition"
                    onLabelChange={(value) =>
                      setThemeLevels((prev) => updateLevelField(sort5(prev, DEFAULT_IMPACT), selectedImpactLevel?.level, "label", value))
                    }
                    onDefinitionChange={(value) =>
                      setThemeLevels((prev) =>
                        updateLevelField(sort5(prev, DEFAULT_IMPACT), selectedImpactLevel?.level, "definition", value)
                      )
                    }
                    saveLabel={themeSaving ? "Saving..." : "Save Theme Definitions"}
                    onSave={saveThemeLevels}
                    disabled={themeSaving || themeLevelsLoading}
                  />
                </div>
              </>
            )}

            {themeLevelsError && <InlineError text={themeLevelsError} />}
            {themeLevelsLoading && <div style={helperText}>Loading impact definitions...</div>}
            {themeMessage && <Status message={themeMessage} />}
          </div>
        </div>
      </section>
    </div>
  );
}

function InlineError({ text }) {
  return <div style={inlineError}>{text}</div>;
}

function LevelSidebar({ title, entries, selectedLevel, onSelect, ariaPrefix }) {
  return (
    <div style={sidebarPanel}>
      <div style={sidebarTitle}>{title}</div>
      <div style={sidebarList}>
        {entries.map((entry) => {
          const selected = Number(entry.level) === Number(selectedLevel);
          return (
            <button
              key={`${title}-${entry.level}`}
              type="button"
              onClick={() => onSelect(entry.level)}
              aria-label={`${ariaPrefix} ${entry.level}`}
              style={selected ? levelItemActive : levelItem}
            >
              <span style={levelMain}>Level {entry.level}</span>
              <span style={levelSub}>{entry.label || `Level ${entry.level}`}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LevelEditor({
  heading,
  labelValue,
  definitionValue,
  labelInputLabel,
  definitionLabel,
  onLabelChange,
  onDefinitionChange,
  saveLabel,
  onSave,
  disabled,
}) {
  return (
    <div style={editorPanel}>
      <h5 style={{ margin: 0 }}>{heading}</h5>
      <Input label={labelInputLabel} value={labelValue} onChange={(event) => onLabelChange(event.target.value)} />
      <label style={editorLabel}>
        <span style={editorLabelText}>{definitionLabel}</span>
        <textarea
          rows={3}
          value={definitionValue}
          onFocus={autoGrow}
          onInput={autoGrow}
          onChange={(event) => onDefinitionChange(event.target.value)}
          style={editorTextarea}
        />
      </label>
      <Button onClick={onSave} variant="primary" disabled={disabled}>{saveLabel}</Button>
    </div>
  );
}

function Status({ message }) {
  const isError = message?.tone === "error";
  return (
    <div
      style={{
        marginTop: spacing.xs,
        padding: spacing.sm,
        borderRadius: 8,
        border: isError ? "1px solid #fecaca" : "1px solid #bbf7d0",
        background: isError ? "#fef2f2" : "#ecfdf3",
        color: isError ? "#b91c1c" : "#166534",
      }}
    >
      {message?.text}
    </div>
  );
}

const pageWrap = { maxWidth: 1100, margin: "0 auto", padding: spacing.md, display: "grid", gap: spacing.sm };
const subtitle = { margin: 0, color: "#64748b", fontSize: 14 };
const sectionCard = { border: "1px solid #e2e8f0", borderRadius: 12, background: "#ffffff", padding: spacing.md, display: "grid", gap: spacing.sm };
const sectionTitle = { margin: 0, fontSize: 18, color: "#0f172a" };
const scoringRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.sm, flexWrap: "wrap" };
const modeGroup = { display: "flex", gap: spacing.md, flexWrap: "wrap", alignItems: "center" };
const radioItem = { display: "inline-flex", gap: spacing.xs, alignItems: "center", fontSize: 14, color: "#334155" };
const bandsHeaderRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.sm, flexWrap: "wrap" };
const bandsGrid = { display: "grid", gridTemplateColumns: "minmax(120px, 1fr) minmax(150px, 1fr) 90px 90px", gap: spacing.xs, alignItems: "center" };
const rangeRow = { display: "flex", alignItems: "center", gap: spacing.xs };
const bandActionCell = { display: "flex", justifyContent: "flex-end" };
const colorInput = { width: 46, height: 32, border: "1px solid #cbd5e1", borderRadius: 6, background: "transparent", cursor: "pointer" };
const matrixWrap = { border: "1px solid #e2e8f0", borderRadius: 10, padding: spacing.sm, overflowX: "auto", background: "#f8fafc" };
const matrixHeadRow = { display: "grid", gridTemplateColumns: "108px repeat(5, 56px)", gap: spacing.xs, alignItems: "center" };
const matrixRow = { display: "grid", gridTemplateColumns: "108px repeat(5, 56px)", gap: spacing.xs, alignItems: "center", marginTop: spacing.xs };
const matrixCorner = { minHeight: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#64748b", fontWeight: 600 };
const matrixAxis = { minHeight: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#475569", fontWeight: 600 };
const matrixCellShell = { width: 56, height: 56, borderRadius: 8, border: "1px solid rgba(148,163,184,0.5)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" };
const matrixCellButton = { width: "100%", height: "100%", border: "none", background: "transparent", fontSize: 13, fontWeight: 700 };
const matrixCellInput = { width: "100%", height: "100%", marginBottom: 0, borderRadius: 0, border: "none", textAlign: "center", fontWeight: 700 };
const templateSection = { border: "1px solid #e2e8f0", borderRadius: 10, padding: spacing.sm, background: "#f8fafc", display: "grid", gap: spacing.xs };
const placeholderReference = {
  border: "1px solid #dbe3f0",
  borderRadius: 8,
  background: "#ffffff",
  padding: spacing.sm,
  display: "grid",
  gap: spacing.xs,
};
const placeholderReferenceTitle = { fontSize: 12, color: "#475569", fontWeight: 700 };
const placeholderReferenceGrid = { display: "flex", flexWrap: "wrap", gap: spacing.xs };
const placeholderCard = {
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  background: "#f8fafc",
  padding: `${spacing.xs}px ${spacing.sm}px`,
  display: "grid",
  gap: spacing.xs / 2,
  minWidth: 160,
  flex: "1 1 180px",
};
const placeholderCardActive = {
  border: "1px solid #16a34a",
  background: "#f0fdf4",
  boxShadow: "inset 0 0 0 1px rgba(22,163,74,0.12)",
};
const placeholderCardHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing.xs };
const placeholderCardLabel = { fontSize: 12, color: "#0f172a", fontWeight: 600 };
const placeholderBadge = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  background: "#16a34a",
  color: "#ffffff",
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
};
const templateButtons = { display: "flex", gap: spacing.xs, justifyContent: "flex-end", flexWrap: "wrap" };
const definitionsGrid = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: spacing.sm, alignItems: "start" };
const definitionPanel = { border: "1px solid #e2e8f0", borderRadius: 10, padding: spacing.sm, background: "#f8fafc", display: "grid", gap: spacing.xs, maxHeight: 560, overflowY: "auto" };
const panelTitle = { margin: 0, fontSize: 16, color: "#0f172a" };
const panelEditorWrap = { display: "grid", gridTemplateColumns: "220px minmax(0, 1fr)", gap: spacing.xs, alignItems: "start" };
const impactHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.xs, flexWrap: "wrap" };
const inlineStrip = { border: "1px solid #dbe3f0", borderRadius: 8, background: "#ffffff", padding: spacing.sm, display: "grid", gap: spacing.xs };
const inlineButtons = { display: "flex", gap: spacing.xs };
const themeTabsWrap = { display: "flex", gap: spacing.xs, flexWrap: "nowrap", overflowX: "auto", paddingBottom: spacing.xs };
const themeTab = { border: "1px solid #cbd5e1", borderRadius: 999, background: "#ffffff", color: "#334155", padding: `${spacing.xs}px ${spacing.sm}px`, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" };
const themeTabActive = { border: "1px solid #0f172a", borderRadius: 999, background: "#0f172a", color: "#ffffff", padding: `${spacing.xs}px ${spacing.sm}px`, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" };
const themeControls = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.xs, flexWrap: "wrap" };
const inlineError = { padding: spacing.sm, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", fontSize: 13 };
const helperText = { fontSize: 12, color: "#6b7280" };
const sidebarPanel = { border: "1px solid #e2e8f0", borderRadius: 8, background: "#ffffff", padding: spacing.sm, display: "grid", gap: spacing.xs, maxHeight: 420, overflowY: "auto" };
const sidebarTitle = { fontSize: 12, color: "#475569", fontWeight: 700 };
const sidebarList = { display: "grid", gap: spacing.xs };
const levelItem = { border: "1px solid #cbd5e1", borderRadius: 8, background: "#ffffff", color: "#1e293b", padding: `${spacing.xs}px ${spacing.sm}px`, textAlign: "left", cursor: "pointer", display: "grid", gap: 2 };
const levelItemActive = { border: "1px solid #0f172a", borderRadius: 8, background: "#e2e8f0", color: "#0f172a", padding: `${spacing.xs}px ${spacing.sm}px`, textAlign: "left", cursor: "pointer", display: "grid", gap: 2 };
const levelMain = { fontSize: 13, fontWeight: 600 };
const levelSub = { fontSize: 12, color: "#64748b" };
const editorPanel = { border: "1px solid #e2e8f0", borderRadius: 8, background: "#ffffff", padding: spacing.sm, display: "grid", gap: spacing.xs };
const editorLabel = { display: "grid", gap: spacing.xs };
const editorLabelText = { fontSize: 13, color: "#374151" };
const editorTextarea = { width: "100%", minHeight: 72, maxHeight: 160, resize: "vertical", borderRadius: 8, border: "1px solid #d1d5db", padding: `${spacing.xs}px ${spacing.sm}px`, boxSizing: "border-box" };
